import type { OptionReorderScope } from "../../../shared/constants/api";
import type { CompanyProductSystemRepository } from "../../../domain/repositories/company/company-product-system-repository";
import { companyProductSystemApiService } from "../../api/company/company-product-system-api-service";
import type {
  ProductConfigDto,
  CategoryDto,
  DeleteProductResult,
  ProductLightDto,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateProductRequest,
  UpdateProductRequest,
  DuplicateProductRequest,
  ProductPaperDto,
  CreateProductPaperRequest,
  SizeTemplateDto,
  CreateSizeTemplateRequest,
  OptionDto,
  OptionAttributeDto,
  OptionAttributeValueDto,
  CreateOptionRequest,
  CreateOptionAttributeRequest,
  CreateOptionAttributeValueRequest,
  PriceCalculationRequest,
  PriceCalculationResult,
  PaperSpecDto,
  MaterialDto,
} from "../../../domain/types/company/company-product-system";

export class CompanyProductSystemRepositoryImpl
  implements CompanyProductSystemRepository
{
  async getProductConfig(companyId: number): Promise<ProductConfigDto> {
    const response =
      await companyProductSystemApiService.getProductConfig(companyId);
    if (!response.data) {
      throw new Error("상품 설정 정보를 가져올 수 없습니다.");
    }
    return response.data;
  }

  async getCategories(companyId: number): Promise<CategoryDto[]> {
    const response =
      await companyProductSystemApiService.getCategories(companyId);
    if (!response.data) {
      throw new Error("카테고리 목록을 가져올 수 없습니다.");
    }
    return response.data;
  }

  async createCategory(
    companyId: number,
    request: CreateCategoryRequest,
  ): Promise<CategoryDto> {
    const response = await companyProductSystemApiService.createCategory(
      companyId,
      request,
    );
    if (!response.data) {
      throw new Error("카테고리 생성에 실패했습니다.");
    }
    return response.data;
  }

  async updateCategory(
    companyId: number,
    categoryId: number,
    request: UpdateCategoryRequest,
  ): Promise<CategoryDto> {
    const response = await companyProductSystemApiService.updateCategory(
      companyId,
      categoryId,
      request,
    );
    if (!response.data) {
      throw new Error("카테고리 수정에 실패했습니다.");
    }
    return response.data;
  }

  async deleteCategory(companyId: number, categoryId: number): Promise<void> {
    await companyProductSystemApiService.deleteCategory(companyId, categoryId);
  }

  async bulkUpdateCategoryOrder(
    companyId: number,
    orders: Array<{ categoryId: number; displayOrder: number }>,
  ): Promise<CategoryDto[]> {
    const response = await companyProductSystemApiService.bulkUpdateCategoryOrder(companyId, orders);
    if (!response.data) {
      throw new Error("카테고리 순서를 변경할 수 없습니다.");
    }
    return response.data;
  }

  async bulkUpdateProductOrder(
    companyId: number,
    orders: Array<{ productId: number; displayOrder: number }>,
  ): Promise<void> {
    await companyProductSystemApiService.bulkUpdateProductOrder(companyId, orders);
  }

  async getProducts(companyId: number): Promise<ProductLightDto[]> {
    const response =
      await companyProductSystemApiService.getProducts(companyId);
    if (!response.data) {
      throw new Error("상품 목록을 가져올 수 없습니다.");
    }
    return response.data;
  }

  async getProduct(companyId: number, productId: number): Promise<any> {
    const response = await companyProductSystemApiService.getProduct(
      companyId,
      productId,
    );
    if (!response.data) {
      throw new Error("상품 정보를 가져올 수 없습니다.");
    }
    return response.data;
  }

  async createProduct(
    companyId: number,
    request: CreateProductRequest,
  ): Promise<any> {
    const response = await companyProductSystemApiService.createProduct(
      companyId,
      request,
    );
    if (!response.data) {
      throw new Error("상품 생성에 실패했습니다.");
    }
    return response.data;
  }

  async updateProduct(
    companyId: number,
    productId: number,
    request: UpdateProductRequest,
  ): Promise<any> {
    const response = await companyProductSystemApiService.updateProduct(
      companyId,
      productId,
      request,
    );
    if (!response.data) {
      throw new Error("상품 수정에 실패했습니다.");
    }
    return response.data;
  }

  async duplicateProduct(
    companyId: number,
    productId: number,
    request: DuplicateProductRequest,
  ): Promise<any> {
    const response = await companyProductSystemApiService.duplicateProduct(
      companyId,
      productId,
      request,
    );
    if (!response.data) {
      throw new Error("상품 복제에 실패했습니다.");
    }
    return response.data;
  }

  async uploadProductThumbnail(
    companyId: number,
    productId: number,
    file: File,
  ): Promise<any> {
    const response = await companyProductSystemApiService.uploadProductThumbnail(
      companyId,
      productId,
      file,
    );
    if (!response.data) {
      throw new Error("썸네일 업로드에 실패했습니다.");
    }
    return response.data;
  }

  async deleteProduct(
    companyId: number,
    productId: number,
    confirmed = false,
  ): Promise<DeleteProductResult> {
    return companyProductSystemApiService.deleteProduct(
      companyId,
      productId,
      confirmed,
    );
  }

  async getProductPapers(
    companyId: number,
    productId: number,
  ): Promise<ProductPaperDto[]> {
    const response = await companyProductSystemApiService.getProductPapers(
      companyId,
      productId,
    );
    if (!response.data) {
      throw new Error("상품 용지 목록을 가져올 수 없습니다.");
    }
    return response.data;
  }

  async createProductPaper(
    companyId: number,
    productId: number,
    request: CreateProductPaperRequest,
  ): Promise<ProductPaperDto> {
    const response = await companyProductSystemApiService.createProductPaper(
      companyId,
      productId,
      request,
    );
    if (!response.data) {
      throw new Error("상품 용지 생성에 실패했습니다.");
    }
    return response.data;
  }

  async updateProductPaper(
    companyId: number,
    productId: number,
    paperId: number,
    request: CreateProductPaperRequest,
  ): Promise<ProductPaperDto> {
    const response = await companyProductSystemApiService.updateProductPaper(
      companyId,
      productId,
      paperId,
      request,
    );
    if (!response.data) {
      throw new Error("상품 용지 수정에 실패했습니다.");
    }
    return response.data;
  }

  async deleteProductPaper(
    companyId: number,
    productId: number,
    paperId: number,
  ): Promise<boolean> {
    const response = await companyProductSystemApiService.deleteProductPaper(
      companyId,
      productId,
      paperId,
    );
    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }
    throw new Error("상품 용지 삭제에 실패했습니다.");
  }

  async getSizeTemplates(
    companyId: number,
    productId: number,
  ): Promise<SizeTemplateDto[]> {
    const response = await companyProductSystemApiService.getSizeTemplates(
      companyId,
      productId,
    );
    if (!response.data) {
      throw new Error("사이즈 템플릿 목록을 가져올 수 없습니다.");
    }
    return response.data;
  }

  async createSizeTemplate(
    companyId: number,
    productId: number,
    request: CreateSizeTemplateRequest,
  ): Promise<SizeTemplateDto> {
    const response = await companyProductSystemApiService.createSizeTemplate(
      companyId,
      productId,
      request,
    );
    if (!response.data) {
      throw new Error("사이즈 템플릿 생성에 실패했습니다.");
    }
    return response.data;
  }

  async updateSizeTemplate(
    companyId: number,
    productId: number,
    sizeTemplateId: number,
    request: CreateSizeTemplateRequest,
  ): Promise<SizeTemplateDto> {
    const response = await companyProductSystemApiService.updateSizeTemplate(
      companyId,
      productId,
      sizeTemplateId,
      request,
    );
    if (!response.data) {
      throw new Error("사이즈 템플릿 수정에 실패했습니다.");
    }
    return response.data;
  }

  async deleteSizeTemplate(
    companyId: number,
    productId: number,
    sizeTemplateId: number,
  ): Promise<boolean> {
    const response = await companyProductSystemApiService.deleteSizeTemplate(
      companyId,
      productId,
      sizeTemplateId,
    );
    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }
    throw new Error("사이즈 템플릿 삭제에 실패했습니다.");
  }

  async getOptions(
    companyId: number,
    productId: number,
  ): Promise<OptionDto[]> {
    const response = await companyProductSystemApiService.getOptions(
      companyId,
      productId,
    );
    if (!response.data) {
      throw new Error("옵션 목록을 가져올 수 없습니다.");
    }
    return response.data;
  }

  async getOption(
    companyId: number,
    productId: number,
    optionId: number,
  ): Promise<OptionDto> {
    const response = await companyProductSystemApiService.getOption(
      companyId,
      productId,
      optionId,
    );
    if (!response.data) {
      throw new Error("옵션 정보를 가져올 수 없습니다.");
    }
    return response.data;
  }

  async createOption(
    companyId: number,
    productId: number,
    request: CreateOptionRequest,
  ): Promise<OptionDto> {
    const response = await companyProductSystemApiService.createOption(
      companyId,
      productId,
      request,
    );
    if (!response.data) {
      throw new Error("옵션 생성에 실패했습니다.");
    }
    return response.data;
  }

  async updateOption(
    companyId: number,
    productId: number,
    optionId: number,
    request: Partial<CreateOptionRequest>,
  ): Promise<OptionDto> {
    const response = await companyProductSystemApiService.updateOption(
      companyId,
      productId,
      optionId,
      request,
    );
    if (!response.data) {
      throw new Error("옵션 수정에 실패했습니다.");
    }
    return response.data;
  }

  async deleteOption(
    companyId: number,
    productId: number,
    optionId: number,
  ): Promise<boolean> {
    const response = await companyProductSystemApiService.deleteOption(
      companyId,
      productId,
      optionId,
    );
    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }
    throw new Error("옵션 삭제에 실패했습니다.");
  }

  async reorderOptionItems(
    companyId: number,
    productId: number,
    scope: OptionReorderScope,
    orderedIds: number[],
  ): Promise<void> {
    await companyProductSystemApiService.reorderOptionItems(companyId, productId, scope, orderedIds);
  }

  async addInsertPaper(
    companyId: number,
    productId: number,
  ): Promise<OptionDto> {
    const response = await companyProductSystemApiService.addInsertPaper(
      companyId,
      productId,
    );
    if (!response.data) {
      throw new Error("간지 추가에 실패했습니다.");
    }
    return response.data;
  }

  async addEndPaper(
    companyId: number,
    productId: number,
  ): Promise<OptionDto> {
    const response = await companyProductSystemApiService.addEndPaper(
      companyId,
      productId,
    );
    if (!response.data) {
      throw new Error("면지 추가에 실패했습니다.");
    }
    return response.data;
  }

  async addPaperOrientation(
    companyId: number,
    productId: number,
  ): Promise<OptionDto> {
    const response = await companyProductSystemApiService.addPaperOrientation(
      companyId,
      productId,
    );
    if (!response.data) {
      throw new Error("용지 방향 추가에 실패했습니다.");
    }
    return response.data;
  }

  async createOptionAttribute(
    companyId: number,
    productId: number,
    optionId: number,
    request: CreateOptionAttributeRequest,
  ): Promise<OptionAttributeDto> {
    const response =
      await companyProductSystemApiService.createOptionAttribute(
        companyId,
        productId,
        optionId,
        request,
      );
    if (!response.data) {
      throw new Error("옵션 속성 생성에 실패했습니다.");
    }
    return response.data;
  }

  async updateOptionAttribute(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    request: CreateOptionAttributeRequest,
  ): Promise<OptionAttributeDto> {
    const response =
      await companyProductSystemApiService.updateOptionAttribute(
        companyId,
        productId,
        optionId,
        attributeId,
        request,
      );
    if (!response.data) {
      throw new Error("옵션 속성 수정에 실패했습니다.");
    }
    return response.data;
  }

  async deleteOptionAttribute(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
  ): Promise<boolean> {
    const response =
      await companyProductSystemApiService.deleteOptionAttribute(
        companyId,
        productId,
        optionId,
        attributeId,
      );
    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }
    throw new Error("옵션 속성 삭제에 실패했습니다.");
  }

  async createOptionAttributeValue(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    request: CreateOptionAttributeValueRequest,
    imageFile?: File,
  ): Promise<OptionAttributeValueDto> {
    const response =
      await companyProductSystemApiService.createOptionAttributeValue(
        companyId,
        productId,
        optionId,
        attributeId,
        request,
        imageFile,
      );
    if (!response.data) {
      throw new Error("옵션 값 생성에 실패했습니다.");
    }
    return response.data;
  }

  async updateOptionAttributeValue(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    valueId: number,
    request: CreateOptionAttributeValueRequest,
    imageFile?: File,
  ): Promise<OptionAttributeValueDto> {
    const response =
      await companyProductSystemApiService.updateOptionAttributeValue(
        companyId,
        productId,
        optionId,
        attributeId,
        valueId,
        request,
        imageFile,
      );
    if (!response.data) {
      throw new Error("옵션 값 수정에 실패했습니다.");
    }
    return response.data;
  }

  async deleteOptionAttributeValue(
    companyId: number,
    productId: number,
    optionId: number,
    attributeId: number,
    valueId: number,
  ): Promise<boolean> {
    const response =
      await companyProductSystemApiService.deleteOptionAttributeValue(
        companyId,
        productId,
        optionId,
        attributeId,
        valueId,
      );
    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }
    throw new Error("옵션 값 삭제에 실패했습니다.");
  }

  async calculatePrice(
    companyId: number,
    productId: number,
    request: PriceCalculationRequest,
  ): Promise<PriceCalculationResult> {
    const response = await companyProductSystemApiService.calculatePrice(
      companyId,
      productId,
      request,
    );
    if (!response.data) {
      throw new Error("가격 계산에 실패했습니다.");
    }
    return response.data;
  }

  async getPaperSpecs(companyId: number, includeAutoGenerated?: boolean): Promise<PaperSpecDto[]> {
    const response =
      await companyProductSystemApiService.getPaperSpecs(companyId, includeAutoGenerated);
    if (!response.data) {
      throw new Error("용지 규격 목록을 가져올 수 없습니다.");
    }
    return response.data;
  }

  async getMaterials(
    companyId: number,
    type?: string,
  ): Promise<MaterialDto[]> {
    const response = await companyProductSystemApiService.getMaterials(
      companyId,
      type,
    );
    if (!response.data) {
      throw new Error("자재 목록을 가져올 수 없습니다.");
    }
    return response.data;
  }

  async getMaterial(
    companyId: number,
    materialId: number,
  ): Promise<MaterialDto> {
    const response = await companyProductSystemApiService.getMaterial(
      companyId,
      materialId,
    );
    if (!response.data) {
      throw new Error("자재 정보를 가져올 수 없습니다.");
    }
    return response.data;
  }

  async createMaterial(
    companyId: number,
    request: { name: string; type: string; paperSpecId?: number; grammage?: number },
  ): Promise<MaterialDto> {
    const response = await companyProductSystemApiService.createMaterial(
      companyId,
      request,
    );
    if (!response.data) {
      throw new Error("자재 생성에 실패했습니다.");
    }
    return response.data;
  }

  async updateMaterial(
    companyId: number,
    materialId: number,
    request: { name: string; paperSpecId?: number },
  ): Promise<MaterialDto> {
    const response = await companyProductSystemApiService.updateMaterial(
      companyId,
      materialId,
      request,
    );
    if (!response.data) {
      throw new Error("자재 수정에 실패했습니다.");
    }
    return response.data;
  }

  async deleteMaterial(
    companyId: number,
    materialId: number,
  ): Promise<boolean> {
    const response = await companyProductSystemApiService.deleteMaterial(
      companyId,
      materialId,
    );
    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }
    throw new Error("자재 삭제에 실패했습니다.");
  }
}

